import { z } from 'zod';

import type { BudgetCalcOptions, CompletionPrinciple, PlanCalcOptions } from './calculations.js';
import { CATEGORY_IDS, categoryCarriesCost } from './contract-document.js';
import type { CategoryId } from './contract-document.js';

/**
 * The configurable financial model (design v8, Area 8 / ADR 0027), schema 2.
 *
 * Nine settings resolved ONCE at load from the multivariate PostHog flag
 * `project-budget-model`; every variant payload carries all nine explicitly.
 * The seventh, the Completion Principle (BR-30), joined with ADR 0041; the
 * eighth, `headlineScope`, with schema 2; the ninth, `paymentSchedule`
 * (BR-31), on 30 Sep 2026 — the first setting that switches a surface on or
 * off rather than shaping the numbers. Unresolved flag or unparsable payload
 * ⇒ CONTROL_BUDGET_MODEL. The config is read only through the accessors below —
 * render sites and calculations never branch on the raw payload (that is what
 * lets a new setting land without touching every card).
 *
 * Ratios are STORED as factors regardless of the display metric; conversion
 * happens on commit and display (margin% = 1 − 1/factor, factor = 1/(1 − m),
 * margin clamped at 95%).
 *
 * Schema 2 replaced the single `factorPerSection` boolean with the per-section
 * `uplift` map, because pilot 2 needed uplift on Own Work and 1.0 on
 * Sub-contractors — a setup one boolean cannot state. Visibility and value are
 * independent: `exp` can be visible at 1.05 while `sub` is hidden at 1.0, so
 * "hidden" does not have to mean "no uplift". A payload that is not
 * `schemaVersion: 2` does not parse and the reader falls back to control.
 */

export const CategoryAvailabilitySchema = z.enum(['unavailable', 'available', 'default']);
export type CategoryAvailability = z.infer<typeof CategoryAvailabilitySchema>;

/** The categories whose availability is configurable (BR-27). Never 'own'. */
export const CONFIGURABLE_CATEGORY_IDS = ['cont', 'exp', 'sub', 'mat'] as const;
export type ConfigurableCategoryId = (typeof CONFIGURABLE_CATEGORY_IDS)[number];

/**
 * The payload vocabulary for the Completion Principle — kebab-case like every
 * other setting (`factor`, `cost`). The calculations keep their own enum
 * (`CompletionPrinciple`, `WORK_COMPLETED`/`COST_TO_COST`); the mapping is
 * `completionPrincipleOf`, the same split as `metric` vs stored factors.
 */
export const CompletionPrincipleSettingSchema = z.enum(['work-completed', 'cost-to-cost']);
export type CompletionPrincipleSetting = z.infer<typeof CompletionPrincipleSettingSchema>;

/**
 * Whether a fixed-price contract offers its payment schedule (BR-31). Off in
 * every shipped variant, including control, until a session asks for it — the
 * feature exists in the code either way; the flag decides who sees the action.
 */
export const PaymentScheduleSettingSchema = z.enum(['on', 'off']);
export type PaymentScheduleSetting = z.infer<typeof PaymentScheduleSettingSchema>;

/** The only payload shape this reader understands (schema 2). */
export const BUDGET_MODEL_SCHEMA_VERSION = 2;

/**
 * One section's uplift. `visible` is the whole switch (ADR 0046): a hidden
 * section carries NO uplift — no pill, operative factor 1.0, seeded at 1.0 —
 * and its `default` is dormant until the section is made visible again.
 */
export const UpliftSectionSchema = z.object({
  /** Whether the factor pill is shown and editable on this section's card. */
  visible: z.boolean(),
  /**
   * The factor of a VISIBLE section: what a fresh contract seeds (CALC-25) and
   * what the reset pill returns to. Ignored while `visible` is false — a hidden
   * section operates at 1.0 (CALC-20, ADR 0046). 1.0 = no uplift.
   */
  default: z.number().min(0.1).max(20),
});
export type UpliftSection = z.infer<typeof UpliftSectionSchema>;

/**
 * One map keyed by section, not two parallel maps — two maps can disagree on
 * their key sets and nothing catches it. Keys are exactly `CATEGORY_IDS`, the
 * same set as `cats`, so a mismatch fails to parse rather than to render.
 */
export const UpliftSchema = z.object({
  own: UpliftSectionSchema,
  cont: UpliftSectionSchema,
  exp: UpliftSectionSchema,
  sub: UpliftSectionSchema,
  mat: UpliftSectionSchema,
});
export type Uplift = z.infer<typeof UpliftSchema>;

/**
 * Category availability (BR-27). `own` is here too, typed as the literal
 * `'default'` — Own Work is always seeded, so no other value is valid, but
 * leaving the key out would give the config two section maps with different key
 * sets and force everything iterating "the five sections" to special-case
 * `own` in one of them. Whether `own` may ever widen — a pure pass-through
 * contract with no Own Work — is D-9 and undecided: do not widen it here.
 * *Absent is not zero*, and nobody has decided which a no-own-work contract is.
 */
export const CatsSchema = z.object({
  own: z.literal('default'),
  cont: CategoryAvailabilitySchema,
  exp: CategoryAvailabilitySchema,
  sub: CategoryAvailabilitySchema,
  mat: CategoryAvailabilitySchema,
});
export type Cats = z.infer<typeof CatsSchema>;

/**
 * Which sections the project-level headline factor is blended over.
 *
 * `blended` is today's behaviour (CALC-13 over every distributed category).
 * `own-work` exists in the vocabulary because per-section uplift lets a payload
 * set `sub: 1.0` while the headline still blends pass-through into the number —
 * which is what Laura objected to. **D-1 has not been decided**, so `own-work`
 * is not yet honoured, and `parseBudgetModelPayload` rejects a payload that
 * asks for it rather than silently blending. See the refinement below.
 */
export const HeadlineScopeSchema = z.enum(['own-work', 'blended']);
export type HeadlineScope = z.infer<typeof HeadlineScopeSchema>;

export const BudgetModelConfigSchema = z
  .object({
    /** Rejects a payload this reader does not understand (schema 1, or future). */
    schemaVersion: z.literal(BUDGET_MODEL_SCHEMA_VERSION),
    /** How ratios are read and typed — storage is always factors. */
    metric: z.enum(['factor', 'margin']),
    /** Denominator of every category allocation bar. */
    budgetBasis: z.enum(['cost', 'revenue']),
    /** Per-section uplift: visibility and value, independently (CALC-20). */
    uplift: UpliftSchema,
    /** Which sections the headline factor blends over — see HeadlineScopeSchema. */
    headlineScope: HeadlineScopeSchema,
    /** Reference figure: a factor when metric=factor, a margin % when metric=margin. */
    target: z.number(),
    /** % of the contract amount guided entry seeds into Contingency; 0 seeds empty. */
    contingencyDefault: z.number().min(0).max(90),
    cats: CatsSchema,
    /**
     * How a fixed-price allocation earns revenue (BR-30, ADR 0041):
     * `work-completed` prices every hour at the contract's average rate
     * (CALC-23); `cost-to-cost` derives revenue from cost — reserved, so it
     * prices nothing yet. Read through `completionPrincipleOf`.
     */
    completionPrinciple: CompletionPrincipleSettingSchema,
    /** The payment schedule action on fixed-price contracts (BR-31). Read through `paymentScheduleEnabled`. */
    paymentSchedule: PaymentScheduleSettingSchema,
  })
  .superRefine((config, ctx) => {
    if (config.metric === 'factor' && (config.target < 0.1 || config.target > 20)) {
      ctx.addIssue({ code: 'custom', path: ['target'], message: 'Factor target must be 0.1–20' });
    }
    if (config.metric === 'margin' && (config.target < 0 || config.target > 95)) {
      ctx.addIssue({ code: 'custom', path: ['target'], message: 'Margin target must be 0–95 %' });
    }
    // The vocabulary is recorded so the payloads can carry it, but nothing
    // branches on it yet. A payload asking for `own-work` would render a
    // blended headline while claiming otherwise — the exact failure schema 2
    // exists to stop — so it falls back to control, loudly, instead.
    if (config.headlineScope === 'own-work') {
      ctx.addIssue({
        code: 'custom',
        path: ['headlineScope'],
        message: "headlineScope 'own-work' is not implemented yet (D-1 undecided) — use 'blended'",
      });
    }
  });
export type BudgetModelConfig = z.infer<typeof BudgetModelConfigSchema>;

/**
 * The control variant — also the fallback whenever the flag is unresolved.
 * MUST stay equal to the `control` payload shipped in PostHog.
 *
 * The `uplift` defaults are the per-section factors the app has always seeded
 * (`CATEGORY_DEFS`, contract-document.ts) — schema 2 moves the source of truth
 * for them into the payload, so control has to restate them exactly or a fresh
 * contract seeds differently than it did under schema 1.
 */
export const CONTROL_BUDGET_MODEL: BudgetModelConfig = {
  schemaVersion: 2,
  metric: 'factor',
  budgetBasis: 'cost',
  uplift: {
    own: { visible: true, default: 2.8 },
    cont: { visible: true, default: 2.1 },
    exp: { visible: true, default: 1.2 },
    sub: { visible: true, default: 1.5 },
    mat: { visible: true, default: 1.3 },
  },
  headlineScope: 'blended',
  target: 2.1,
  contingencyDefault: 10,
  cats: { own: 'default', cont: 'default', exp: 'available', sub: 'available', mat: 'available' },
  completionPrinciple: 'work-completed',
  paymentSchedule: 'off',
};

export type BudgetModelSource = 'flag' | 'control';

/** Parse a flag payload; anything that does not validate falls back to control. */
export const parseBudgetModelPayload = (
  payload: unknown,
): { config: BudgetModelConfig; source: BudgetModelSource } => {
  const parsed = BudgetModelConfigSchema.safeParse(payload);
  return parsed.success
    ? { config: parsed.data, source: 'flag' }
    : { config: CONTROL_BUDGET_MODEL, source: 'control' };
};

// ─── Metric conversions (CALC-10 is the only margin formula) ───

export const MAX_MARGIN_PCT = 95;

export const marginPctFromFactor = (factor: number): number => (1 - 1 / factor) * 100;

/** Inverse of CALC-10, clamped at 95% margin (factor ≤ 20). */
export const factorFromMarginPct = (marginPct: number): number => {
  const clamped = Math.min(MAX_MARGIN_PCT, Math.max(0, marginPct));
  return 1 / (1 - clamped / 100);
};

// ─── Accessors — the only way the config is read ───

/** The reference target, normalized to a factor whatever the metric. */
export const targetFactorOf = (config: BudgetModelConfig): number =>
  config.metric === 'factor' ? config.target : factorFromMarginPct(config.target);

/**
 * Whether the configured target can be read as a goal at all.
 *
 * The budgeted factor is revenue over cost, so it can only move if some factor
 * can move — which, since every hidden section is pinned at 1.0, means some
 * section's uplift is editable.
 *
 * Since ADR 0046 a hidden section operates at 1.0 whatever its `default`, so
 * the second way is gone: with every section hidden the budgeted factor is
 * pinned at ×1 by construction, a `target` of 2.1 is unreachable however the
 * budget is built, and the trend against it would sit permanently on the coral
 * "down" arrow — a number displayed in a state where it cannot mean anything.
 * Callers suppress the target reading and the trend instead.
 *
 * This is a real configuration: the `project-budget-model` flag is used to
 * assemble a variant per customer interview, and "uplift off" is one of the
 * combinations that can be chosen.
 */
export const targetIsOperative = (config: BudgetModelConfig): boolean =>
  CATEGORY_IDS.some((id) => upliftIsVisible(config, id));

export type TargetReading = {
  metric: BudgetModelConfig['metric'];
  factor: number;
  marginPct: number;
};

/** The totals-row target in both units; render per `metric`. */
export const targetReading = (config: BudgetModelConfig): TargetReading => {
  const factor = targetFactorOf(config);
  return {
    metric: config.metric,
    factor,
    marginPct: config.metric === 'margin' ? config.target : marginPctFromFactor(factor),
  };
};

const isConfigurable = (id: CategoryId): id is ConfigurableCategoryId =>
  (CONFIGURABLE_CATEGORY_IDS as readonly string[]).includes(id);

/** 'unavailable' always wins; Own Work is never configurable. */
export const isCategoryAvailable = (config: BudgetModelConfig, id: CategoryId): boolean =>
  !isConfigurable(id) || config.cats[id] !== 'unavailable';

/** Whether a category is present (not hidden) on a freshly created contract. */
export const categoryPresentOnCreate = (config: BudgetModelConfig, id: CategoryId): boolean =>
  !isConfigurable(id) || config.cats[id] === 'default';

/** The `seed.hiddenCategories` list for creating a contract under this config. */
export const hiddenOnCreate = (config: BudgetModelConfig): ConfigurableCategoryId[] =>
  CONFIGURABLE_CATEGORY_IDS.filter((id) => !categoryPresentOnCreate(config, id));

/** Share (0–0.9) of the contract amount guided entry seeds into Contingency. */
export const contingencySeedShare = (config: BudgetModelConfig): number =>
  isCategoryAvailable(config, 'cont') ? config.contingencyDefault / 100 : 0;

/**
 * Whether this section's factor pill is shown and editable (CALC-20). Never
 * for a cost-less section (Contingency, BR-14): it has no cost to uplift, so
 * the payload's `uplift.cont` is carried but ignored.
 */
export const upliftIsVisible = (config: BudgetModelConfig, id: CategoryId): boolean =>
  categoryCarriesCost(id) && config.uplift[id].visible;

/** The payload's factor for a VISIBLE section — its seed, and what its reset pill returns to. */
export const defaultFactorOf = (config: BudgetModelConfig, id: CategoryId): number =>
  config.uplift[id].default;

/** The factor a hidden section operates at and is seeded with (ADR 0046): no uplift. */
export const HIDDEN_UPLIFT_FACTOR = 1;

/**
 * CALC-25 — the factor a section is seeded with: the payload's `default` while
 * its uplift is visible, 1.0 while hidden. One definition for a fresh contract
 * (`seedCategoryFactors`) and for a category added back to one (the tile).
 */
export const seedFactorOf = (config: BudgetModelConfig, id: CategoryId): number =>
  config.uplift[id].visible ? config.uplift[id].default : HIDDEN_UPLIFT_FACTOR;

/**
 * CALC-20 — the operative factor for a section: the stored one while the user
 * can edit it, otherwise 1.0. Hidden means no uplift (ADR 0046) — the stored
 * value is left in the document and comes back when the section is made
 * visible again.
 */
export const effectiveFactorValue = (
  config: BudgetModelConfig,
  id: CategoryId,
  storedFactor: number,
): number => (config.uplift[id].visible ? storedFactor : HIDDEN_UPLIFT_FACTOR);

/**
 * CALC-25 — the per-section factors a fresh contract seeds under this config.
 * The client sends these as `seed.categoryFactors`; the API stays config-blind.
 */
export const seedCategoryFactors = (config: BudgetModelConfig): Record<CategoryId, number> =>
  Object.fromEntries(CATEGORY_IDS.map((id) => [id, seedFactorOf(config, id)])) as Record<
    CategoryId,
    number
  >;

/**
 * Which sections the headline factor blends over. Always `blended` today —
 * `own-work` does not parse (D-1). Callers read this rather than assuming.
 */
export const headlineScopeOf = (config: BudgetModelConfig): HeadlineScope => config.headlineScope;

/** The options object the shared calculations take (CALC-19/20). */
export const calcOptionsOf = (config: BudgetModelConfig): BudgetCalcOptions => ({
  availability: {
    own: true,
    cont: isCategoryAvailable(config, 'cont'),
    exp: isCategoryAvailable(config, 'exp'),
    sub: isCategoryAvailable(config, 'sub'),
    mat: isCategoryAvailable(config, 'mat'),
  },
  // Only the sections the user cannot edit are pinned — at 1.0, no uplift
  // (ADR 0046); the rest use whatever the document stores. An empty map (every
  // section visible) is the schema-1 `factorPerSection: true` behaviour exactly.
  fixedFactors: Object.fromEntries(
    CATEGORY_IDS.filter((id) => !config.uplift[id].visible).map((id) => [id, HIDDEN_UPLIFT_FACTOR]),
  ),
});

/** BR-30 — the configured Completion Principle in the calculations' vocabulary. */
export const completionPrincipleOf = (config: BudgetModelConfig): CompletionPrinciple =>
  config.completionPrinciple === 'cost-to-cost' ? 'COST_TO_COST' : 'WORK_COMPLETED';

/** Whether the Payment schedule action is offered on a fixed-price contract (BR-31). */
export const paymentScheduleEnabled = (config: BudgetModelConfig): boolean =>
  config.paymentSchedule === 'on';

/**
 * The plan-side options (CALC-23 pricing): the completion principle from the
 * config plus the caller's evaluation day, which stays injected (D14) — never
 * a clock inside a pure function.
 */
export const planCalcOptionsOf = (
  config: BudgetModelConfig,
  today: string | undefined,
): PlanCalcOptions => ({
  ...(today === undefined ? {} : { today }),
  completionPrinciple: completionPrincipleOf(config),
});
